import { type WikiThreadKind, wikiCommentBodySchema } from "@esi/shared";
import {
  Bot,
  CircleQuestionMark,
  History,
  Lock,
  MessageSquare,
  MessageSquareWarning,
} from "lucide-react";
import { type RefObject, useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useMediaQuery } from "@/hooks/use-media-query";
import { cn } from "@/lib/utils";
import { INSTRUCTION_MAX } from "./instructions.ts";
import { type Passage, passageOf } from "./selection.ts";

/**
 * The reading view's selection popover (wiki-collaboration-ux D11). One component, two modes
 * decided by the caller and never offered together: `instruct` (the creator's own draft: one
 * action, "Instruct the agent", whose instruction goes to the agent, not to people) and
 * `comment` (the review: Comment, Question or Request a change, the last optionally asking the
 * agent to revise). It appears on a non-empty selection inside `container`, is reached with
 * `Shift+F10` on a selection, and is a bottom bar under 640px (under 1024px in the review).
 */
export function SelectionPopover(
  props: {
    container: RefObject<HTMLElement | null>;
    /** The action is unavailable (the tray is full): it says so instead of opening. */
    disabled?: string | undefined;
  } & (
    | { mode: "instruct"; onInstruct: (passage: Passage, text: string) => void }
    | {
        mode: "comment";
        /** A change request may be addressed to the agent (D15). */
        agentEligible: boolean;
        /** The kinds offered; a published page takes no change request (S4). All by default. */
        kinds?: readonly WikiThreadKind[];
        /** The composer's line on who will see it; the review's own by default. */
        visibleLine?: string;
        /** Opens the passage's history (S5, D17); absent: not offered. */
        onHistory?: (passage: Passage) => void;
        /** Posts the thread; a refusal keeps the composer open with the text. */
        onComment: (
          passage: Passage,
          kind: WikiThreadKind,
          body: string,
          forAgent: boolean,
        ) => Promise<unknown>;
      }
  ),
) {
  const { container, disabled } = props;
  const { t } = useTranslation();
  const id = useId();
  // The review's sheet layout starts below 1024 px (D15): its popover is a bottom bar there too.
  const phone = useMediaQuery(
    props.mode === "comment" ? "(max-width: 1023px)" : "(max-width: 639px)",
  );
  const [passage, setPassage] = useState<Passage | null>(null);
  const [at, setAt] = useState<{ top: number; left: number } | null>(null);
  const [composing, setComposing] = useState(false);
  const [text, setText] = useState("");
  // Comment mode: the kind chosen, and whether a change request also asks the agent.
  const [kind, setKind] = useState<WikiThreadKind>("comment");
  const [forAgent, setForAgent] = useState(false);
  const [busy, setBusy] = useState(false);
  const field = useRef<HTMLTextAreaElement>(null);

  const read = useCallback(() => {
    const root = container.current;
    const selection = window.getSelection();
    if (!root || !selection || selection.rangeCount === 0) {
      return null;
    }
    const range = selection.getRangeAt(0);
    const found = passageOf(root, range);
    if (!found) {
      return null;
    }
    const box = range.getBoundingClientRect();
    const frame = root.getBoundingClientRect();
    return {
      passage: found,
      at: {
        top: box.bottom - frame.top + 6,
        left: Math.max(0, Math.min(box.left - frame.left, frame.width - 320)),
      },
    };
  }, [container]);

  useEffect(() => {
    const root = container.current;
    if (!root) {
      return;
    }
    const update = (e: Event) => {
      if (
        composing ||
        (e.target instanceof Element && e.target.closest("[data-selection-ignore]"))
      ) {
        return;
      }
      const next = read();
      setPassage(next?.passage ?? null);
      setAt(next?.at ?? null);
    };
    // Shift+F10 on keydown, where the browser would open its context menu.
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "F10" && e.shiftKey) {
        const next = read();
        if (next && !disabled) {
          e.preventDefault();
          setPassage(next.passage);
          setAt(next.at);
          // Instruct opens its composer; Comment offers its three kinds first.
          setComposing(props.mode === "instruct");
          if (props.mode === "comment") {
            requestAnimationFrame(() =>
              document
                .querySelector<HTMLButtonElement>(`[data-popover="${CSS.escape(id)}"] button`)
                ?.focus(),
            );
          }
        }
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (!(e.key === "F10" && e.shiftKey)) {
        update(e);
      }
    };
    root.addEventListener("mouseup", update);
    root.addEventListener("keydown", onKeyDown);
    root.addEventListener("keyup", onKeyUp);
    return () => {
      root.removeEventListener("mouseup", update);
      root.removeEventListener("keydown", onKeyDown);
      root.removeEventListener("keyup", onKeyUp);
    };
  }, [container, read, composing, disabled, props.mode, id]);

  // Crossing the phone breakpoint moves the popover in or out of its portal (a remount), so the
  // open composer takes focus back.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `phone` re-runs it after the move.
  useEffect(() => {
    if (composing) {
      field.current?.focus();
    }
  }, [composing, phone]);

  const close = () => {
    setComposing(false);
    setPassage(null);
    setText("");
    setKind("comment");
    setForAgent(false);
  };
  if (!passage) {
    return null;
  }
  const comment = props.mode === "comment" ? props : null;
  const max = comment ? 2000 : INSTRUCTION_MAX;
  const tooLong = Array.from(text).length > max;
  const KIND_ICON = {
    comment: MessageSquare,
    question: CircleQuestionMark,
    change_request: MessageSquareWarning,
  } as const;
  const title = comment ? t(`wiki.review.passage.${kind}`) : t("wiki.author.instruct.action");
  const place = phone
    ? "fixed inset-x-0 bottom-0 z-30 rounded-t-xl border-t pb-[max(0.75rem,env(safe-area-inset-bottom))]"
    : "absolute z-20 w-80 rounded-lg border shadow-(--shadow-overlay)";
  const popover = (
    <div
      data-esi-content="wiki"
      data-selection-ignore=""
      data-popover={id}
      role="dialog"
      aria-label={
        comment?.kinds?.length === 0
          ? t("wiki.history.passage.title")
          : comment
            ? t("wiki.review.passage.label")
            : t("wiki.author.instruct.action")
      }
      className={cn("border-line bg-surface p-3", place)}
      style={phone || !at ? undefined : { top: at.top, left: at.left }}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.stopPropagation();
          close();
        }
      }}
    >
      {!composing && comment ? (
        <div className="flex flex-wrap gap-1">
          {(comment.kinds ?? (["comment", "question", "change_request"] as const)).map((k) => {
            const Icon = KIND_ICON[k];
            return (
              <Button
                key={k}
                type="button"
                size="sm"
                variant="ghost"
                disabled={!!disabled}
                onClick={() => {
                  setKind(k);
                  setComposing(true);
                }}
              >
                <Icon aria-hidden="true" strokeWidth={1.75} />
                {t(`wiki.review.passage.action.${k}`)}
              </Button>
            );
          })}
          {comment.onHistory ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                const onHistory = comment.onHistory;
                window.getSelection()?.removeAllRanges();
                close();
                onHistory?.(passage);
              }}
            >
              <History aria-hidden="true" strokeWidth={1.75} />
              {t("wiki.history.passage.action")}
            </Button>
          ) : null}
        </div>
      ) : composing && comment ? (
        <form
          className="flex flex-col gap-2.5"
          onSubmit={async (e) => {
            e.preventDefault();
            const body = wikiCommentBodySchema.safeParse(text);
            if (!body.success || busy) {
              return;
            }
            setBusy(true);
            try {
              await comment.onComment(
                passage,
                kind,
                body.data,
                kind === "change_request" && comment.agentEligible && forAgent,
              );
              window.getSelection()?.removeAllRanges();
              close();
            } catch {
              // The caller shows the refusal; the text stays.
            } finally {
              setBusy(false);
            }
          }}
        >
          <span className="text-[13px] font-medium text-fg">{title}</span>
          <blockquote className="line-clamp-3 border-l-2 border-line-strong pl-2.5 text-[13px] text-fg-secondary">
            {passage.quote}
          </blockquote>
          <label htmlFor={`${id}-text`} className="sr-only">
            {title}
          </label>
          <Textarea
            ref={field}
            id={`${id}-text`}
            rows={3}
            value={text}
            disabled={busy}
            aria-invalid={tooLong || undefined}
            onChange={(e) => setText(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.currentTarget.form?.requestSubmit();
              }
            }}
          />
          {kind === "change_request" && comment.agentEligible ? (
            <div className="flex items-start gap-2">
              <Checkbox
                id={`${id}-agent`}
                checked={forAgent}
                disabled={busy}
                onCheckedChange={(on) => setForAgent(on === true)}
              />
              <Label htmlFor={`${id}-agent`} className="text-xs font-normal">
                {t("wiki.review.passage.askAgent")}
              </Label>
            </div>
          ) : null}
          <p className={cn("text-xs", tooLong ? "text-danger-fg" : "text-fg-muted")}>
            {tooLong
              ? t("wiki.threads.invalid")
              : (comment.visibleLine ?? t("wiki.review.passage.visible"))}
          </p>
          <div className="flex justify-end gap-1.5">
            <Button type="button" size="sm" variant="ghost" onClick={close}>
              {t("wiki.author.instruct.cancel")}
            </Button>
            <Button
              type="submit"
              size="sm"
              variant="outline"
              disabled={!text.trim() || tooLong || busy}
            >
              {t("wiki.threads.post")}
            </Button>
          </div>
        </form>
      ) : !composing ? (
        <div className="flex flex-col gap-1">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="self-start"
            disabled={!!disabled}
            onClick={() => setComposing(true)}
          >
            <Bot aria-hidden="true" strokeWidth={1.75} />
            {t("wiki.author.instruct.action")}
          </Button>
          <p className="text-xs text-fg-muted">{disabled ?? t("wiki.author.instruct.hint")}</p>
        </div>
      ) : (
        <form
          className="flex flex-col gap-2.5"
          onSubmit={(e) => {
            e.preventDefault();
            if (text.trim() && !tooLong && props.mode === "instruct") {
              props.onInstruct(passage, text.trim());
              window.getSelection()?.removeAllRanges();
              close();
            }
          }}
        >
          <div className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-1.5 text-[13px] font-medium text-fg">
              <Bot aria-hidden="true" className="size-4 text-fg-muted" strokeWidth={1.75} />
              {t("wiki.author.instruct.action")}
            </span>
            <span className="text-xs text-fg-muted">{t("wiki.author.instruct.notSent")}</span>
          </div>
          <blockquote className="line-clamp-3 border-l-2 border-line-strong pl-2.5 text-[13px] text-fg-secondary">
            {passage.quote}
          </blockquote>
          <label htmlFor={`${id}-text`} className="sr-only">
            {t("wiki.author.instruct.label")}
          </label>
          <Textarea
            ref={field}
            id={`${id}-text`}
            rows={2}
            value={text}
            aria-invalid={tooLong || undefined}
            aria-describedby={`${id}-hint`}
            placeholder={t("wiki.author.instruct.label")}
            onChange={(e) => setText(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.currentTarget.form?.requestSubmit();
              }
            }}
          />
          <p
            id={`${id}-hint`}
            className={cn(
              "flex items-center gap-1.5 text-xs",
              tooLong ? "text-danger-fg" : "text-fg-muted",
            )}
          >
            <Lock aria-hidden="true" className="size-3" strokeWidth={1.75} />
            {tooLong ? t("wiki.author.instruct.tooLong") : t("wiki.author.instruct.hint")}
          </p>
          <div className="flex justify-end gap-1.5">
            <Button type="button" size="sm" variant="ghost" onClick={close}>
              {t("wiki.author.instruct.cancel")}
            </Button>
            <Button type="submit" size="sm" variant="outline" disabled={!text.trim() || tooLong}>
              {t("wiki.author.instruct.add")}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
  // The phone's bottom bar is fixed to the viewport: rendered outside the page, so a containing
  // block around it (the review's `.wide-host` scroll area, #379) cannot capture it; inside a
  // modal sheet (the conversation's preview) it stays in the sheet, which traps focus and pointer.
  const layer = phone ? (container.current?.closest("[role=dialog]") ?? document.body) : null;
  return layer ? createPortal(popover, layer) : popover;
}
